


import { Component, Vue } from "vue-property-decorator";

import { ValidateEmail } from "@Internal/Utils";
import { Users, Account } from "App";
import { I18n, $t } from "App/IOC";

import { Password, CustomError, User } from "App/Models";

/* Login. Two-factor is OPTIONAL:
   1. credentials — email + password. A probe (Account.Login) says whether a code is required.
                    When 2FA is off the probe answers "none" and the session is minted right away.
   2. totp        — only when 2FA is enabled: enter a 6-digit code (or a recovery code).
   The password is only ever held in memory for the duration of the flow. */
type Step = "credentials" | "totp";

@Component
export default class SignInForm extends Vue {
    private step: Step = "credentials";

    private email: string = "";
    private password: string = "";
    private code: string = "";
    private passwordShown: boolean = false;

    private error: string = "";
    private busy: boolean = false;

    private get credentialsValid(): boolean {
        return !( ValidateEmail( this.email ) instanceof CustomError )
            && this.password.trim() !== "";
    }

    /* The code can be a 6-digit TOTP or a recovery code. */
    private get loginCodeValid(): boolean {
        const value = this.code.trim();

        return /^\d{6}$/.test( value )
            || value.replace( /[^A-Za-z0-9]/g, "" ).length >= 8;
    }

    private passwordModel(): Password {
        return new Password( this.password );
    }

    private message( error: CustomError ): string {
        /* Account.Login forwards a raw message key; Users.Authenticate already translates. $t is
           idempotent (an already-translated string isn't a key, so it passes through), so
           translating here is safe for both. */
        return error.Message !== undefined && error.Message !== ""
            ? $t( error.Message )
            : $t( "SIGNIN_invalidCredentials" );
    }

    /* Step 1 — verify the password and learn whether a second factor is required. */
    private async submitCredentials(): Promise<void> {
        if( this.busy || !this.credentialsValid )
            return;

        this.busy = true;
        this.error = "";

        const [ challenge, error ] = await Account.Login( this.email, this.passwordModel() );

        if( error !== undefined ) {
            this.busy = false;
            this.error = this.message( error );
            return;
        }

        if( challenge === undefined ) {
            this.busy = false;
            this.error = $t( "SIGNIN_invalidCredentials" );
            return;
        }

        this.code = "";

        if( challenge.Status === "totp" ) {
            this.busy = false;
            this.step = "totp";
            return;
        }

        /* 2FA off: authenticate immediately (busy stays true through the call). */
        await this.authenticate( "" );
    }

    /* Step 2 — 2FA enabled: enter a code to mint the session. */
    private async submitTotp(): Promise<void> {
        if( this.busy || !this.loginCodeValid )
            return;

        await this.authenticate( this.code.trim() );
    }

    private async authenticate( code: string ): Promise<void> {
        this.busy = true;
        this.error = "";

        const error = await Users.Authenticate( this.email, this.passwordModel(), code );

        this.busy = false;

        if( error !== undefined ) {
            this.error = this.message( error );
            return;
        }

        this.redirect();
    }

    private redirect(): void {
        this.$i18n.locale = I18n.Locale.Id;

        const user = Users.GetCurrent();

        if( user !== undefined && ( user as User ).FirstName !== undefined )
            this.$router.replace( "/" );
        else
            this.$router.replace( "/user/registration" );
    }

    private backToCredentials(): void {
        this.step = "credentials";
        this.code = "";
        this.error = "";
    }
}
